import React, { useEffect, useState } from "react";
import {
  Box,
  Dialog,
  IconButton,
  Skeleton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { getFileContent as getPresupuestoFiles } from "../../api/Proyectos/projectFilesApi";
import { getProductMuestras } from "../../api/Muestras/muestraApi";
import { useTranslation } from "react-i18next";

const IMAGE_EXT = /\.(jpe?g|png|webp|gif|bmp)$/i;

const isImageFile = (file) =>
  file?.mimeType?.includes("image") || IMAGE_EXT.test(file?.name || file?.fileName || "");

const loadMuestraImage = async (muestraId) => {
  const token = localStorage.getItem("token");
  const listRes = await fetch(
    `${process.env.REACT_APP_API_URL}/api/files/muestra/${muestraId}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );

  if (!listRes.ok) return null;

  const files = await listRes.json();
  const imgFile = (files || []).find(isImageFile);
  if (!imgFile?.path) return null;

  const fileRes = await fetch(
    `${process.env.REACT_APP_API_URL}/api/files/readfile?path=${encodeURIComponent(imgFile.path)}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );

  if (!fileRes.ok) return null;

  const fileData = await fileRes.json();
  if (!fileData?.data) return null;

  return `data:${fileData.mimeType || imgFile.mimeType || "image/jpeg"};base64,${fileData.data}`;
};

const RankingVentasFoto = ({ idMuestra, idProducto, numeroPresupuesto, label }) => {
  const { t } = useTranslation();
  const [src, setSrc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setSrc(null);

      try {
        // 1) Presupuesto (carpeta Proyectos) — igual que Critical Path
        if (numeroPresupuesto) {
          const files = await getPresupuestoFiles(numeroPresupuesto);
          const imgFile = (files || []).find(isImageFile);
          if (!cancelled && imgFile?.data && imgFile?.mimeType) {
            setSrc(`data:${imgFile.mimeType};base64,${imgFile.data}`);
            return;
          }
        }

        // 2) Muestra directa
        if (idMuestra) {
          const url = await loadMuestraImage(idMuestra);
          if (!cancelled && url) {
            setSrc(url);
            return;
          }
        }

        // 3) Primera muestra del producto
        if (idProducto) {
          const muestras = await getProductMuestras(idProducto);
          const first = Array.isArray(muestras) ? muestras[0] : null;
          const muestraId = first?.ID_MUESTRA ?? first?.id ?? null;
          if (muestraId) {
            const url = await loadMuestraImage(muestraId);
            if (!cancelled && url) {
              setSrc(url);
              return;
            }
          }
        }
      } catch (err) {
        if (err?.response?.status !== 404) {
          console.warn("RankingVentasFoto:", err);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (numeroPresupuesto || idMuestra || idProducto) {
      load();
    } else {
      setLoading(false);
      setSrc(null);
    }

    return () => {
      cancelled = true;
    };
  }, [idMuestra, idProducto, numeroPresupuesto]);

  if (loading) {
    return (
      <Skeleton
        variant="rounded"
        width={48}
        height={64}
        sx={{ borderRadius: 1.5 }}
      />
    );
  }

  if (!src) {
    return (
      <Box
        sx={{
          width: 48,
          height: 64,
          borderRadius: 1.5,
          border: "1px dashed",
          borderColor: "divider",
          bgcolor: "action.hover",
        }}
      />
    );
  }

  return (
    <>
      <Box
        component="img"
        src={src}
        alt={label || ""}
        onClick={(e) => {
          e.stopPropagation();
          setPreviewOpen(true);
        }}
        sx={{
          width: 48,
          height: 64,
          objectFit: "cover",
          borderRadius: 1.5,
          border: "1px solid",
          borderColor: "divider",
          display: "block",
          cursor: "zoom-in",
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          transition: "transform 0.15s ease, box-shadow 0.15s ease",
          "&:hover": {
            transform: "scale(1.06)",
            boxShadow: 4,
          },
        }}
      />

      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="md"
        fullWidth
        onClick={(e) => e.stopPropagation()}
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            bgcolor: "background.paper",
            borderTop: "4px solid",
            borderColor: "primary.main",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography variant="subtitle2" fontWeight={700} noWrap sx={{ pr: 2 }}>
            {label || t("rankingVentas.photo")}
          </Typography>
          <IconButton
            size="small"
            onClick={() => setPreviewOpen(false)}
            aria-label={t("common.close")}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box
          sx={{
            p: 2,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            bgcolor: "action.hover",
            minHeight: 320,
          }}
        >
          <Box
            component="img"
            src={src}
            alt={label || ""}
            sx={{
              maxWidth: "100%",
              maxHeight: "70vh",
              objectFit: "contain",
              borderRadius: 1,
            }}
          />
        </Box>
      </Dialog>
    </>
  );
};

export default RankingVentasFoto;
